import { useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== HOTEL BELL SOUND ====================
// Creates a classic hotel reception bell "ding" sound using Web Audio API

let audioContext = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Play hotel bell sound - classic "ding" like a reception bell
const playHotelBellSound = () => {
  try {
    const ctx = getAudioContext();
    
    // Resume audio context if suspended (required by some browsers)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const now = ctx.currentTime;
    
    // Create oscillator for the main bell tone
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    // Bell frequency - E6 note (classic hotel bell pitch)
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(1318.51, now); // E6
    
    // Add slight frequency decay for more realistic bell sound
    oscillator.frequency.exponentialRampToValueAtTime(1200, now + 0.3);
    
    // Create the bell envelope - quick attack, longer decay
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.4, now + 0.01); // Quick attack
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 1.5); // Slow decay
    
    // Add harmonics for richer bell tone
    const harmonic1 = ctx.createOscillator();
    const harmonic1Gain = ctx.createGain();
    harmonic1.type = 'sine';
    harmonic1.frequency.setValueAtTime(2637.02, now); // E7 (octave up)
    harmonic1Gain.gain.setValueAtTime(0, now);
    harmonic1Gain.gain.linearRampToValueAtTime(0.15, now + 0.01);
    harmonic1Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
    
    // Second harmonic
    const harmonic2 = ctx.createOscillator();
    const harmonic2Gain = ctx.createGain();
    harmonic2.type = 'sine';
    harmonic2.frequency.setValueAtTime(3951.07, now); // B7
    harmonic2Gain.gain.setValueAtTime(0, now);
    harmonic2Gain.gain.linearRampToValueAtTime(0.08, now + 0.01);
    harmonic2Gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
    
    // Connect main oscillator
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    // Connect harmonics
    harmonic1.connect(harmonic1Gain);
    harmonic1Gain.connect(ctx.destination);
    harmonic2.connect(harmonic2Gain);
    harmonic2Gain.connect(ctx.destination);
    
    // Start all oscillators
    oscillator.start(now);
    harmonic1.start(now);
    harmonic2.start(now);
    
    // Stop oscillators after decay
    oscillator.stop(now + 1.5);
    harmonic1.stop(now + 0.8);
    harmonic2.stop(now + 0.5);
    
    // Clean up
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
      harmonic1.disconnect();
      harmonic1Gain.disconnect();
      harmonic2.disconnect();
      harmonic2Gain.disconnect();
    };
    
    return true;
  } catch (error) {
    console.error('Error playing bell sound:', error);
    return false;
  }
};

// ==================== NOTIFICATION MESSAGES ====================

// Notification messages by language
const notificationMessages = {
  es: {
    preparando: {
      title: '👨‍🍳 ¡Tu pedido se está preparando!',
      body: 'El equipo de cocina está trabajando en tu pedido'
    },
    listo: {
      title: '✅ ¡Tu pedido está listo!',
      body: 'Tu pedido está listo para servir. ¡Buen provecho!'
    },
    entregado: {
      title: '🍽️ Pedido entregado',
      body: '¡Esperamos que disfrutes tu comida!'
    }
  },
  en: {
    preparando: {
      title: '👨‍🍳 Your order is being prepared!',
      body: 'The kitchen team is working on your order'
    },
    listo: {
      title: '✅ Your order is ready!',
      body: 'Your order is ready to be served. Enjoy!'
    },
    entregado: {
      title: '🍽️ Order delivered',
      body: 'We hope you enjoy your meal!'
    }
  },
  cat: {
    preparando: {
      title: '👨‍🍳 La teva comanda s\'està preparant!',
      body: 'L\'equip de cuina està treballant en la teva comanda'
    },
    listo: {
      title: '✅ La teva comanda està llesta!',
      body: 'La teva comanda està llesta per servir. Bon profit!'
    },
    entregado: {
      title: '🍽️ Comanda entregada',
      body: 'Esperem que gaudeixis del teu àpat!'
    }
  }
};

// Show a notification with hotel bell sound
const showNotification = (title, body, playSound = true) => {
  // Play the hotel bell sound first (even if notifications are not granted)
  if (playSound) {
    playHotelBellSound();
  }
  
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const notification = new Notification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        vibrate: [200, 100, 200],
        tag: `order-status-${Date.now()}`,
        renotify: true,
        silent: true // We handle sound ourselves with the bell
      });

      setTimeout(() => notification.close(), 6000);

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  }
};

// Background notification monitor hook - runs even when CustomerOrdersPanel is closed
export const useBackgroundOrderNotifications = (mesaNumero, language = 'es') => {
  const previousOrdersRef = useRef({});
  const isInitializedRef = useRef(false);

  const checkForUpdates = useCallback(async () => {
    if (!mesaNumero) return;
    if ('Notification' in window && Notification.permission !== 'granted') return;

    try {
      const response = await fetch(`${API_URL}/api/mesa/${mesaNumero}/session`);
      if (!response.ok) return;

      const data = await response.json();
      const orders = data.orders || [];
      const messages = notificationMessages[language] || notificationMessages.es;

      // First call: just initialize
      if (!isInitializedRef.current) {
        orders.forEach(order => {
          previousOrdersRef.current[order.id] = order.estado;
        });
        isInitializedRef.current = true;
        return;
      }

      // Check for status changes
      orders.forEach(order => {
        const previousStatus = previousOrdersRef.current[order.id];
        const currentStatus = order.estado;

        if (previousStatus && previousStatus !== currentStatus) {
          const message = messages[currentStatus];
          if (message) {
            showNotification(message.title, message.body);
          }
        }

        previousOrdersRef.current[order.id] = currentStatus;
      });
    } catch (error) {
      console.error('Background order check error:', error);
    }
  }, [mesaNumero, language]);

  useEffect(() => {
    if (!mesaNumero) return;

    // Initial check
    checkForUpdates();

    // Poll every 15 seconds in background
    const interval = setInterval(checkForUpdates, 15000);

    return () => clearInterval(interval);
  }, [mesaNumero, checkForUpdates]);
};

// Request notification permission
export const requestNotificationPermission = async () => {
  // Check if notifications are supported
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  // Already granted
  if (Notification.permission === 'granted') {
    return true;
  }

  // Already denied - can't ask again
  if (Notification.permission === 'denied') {
    console.log('Notifications were previously denied');
    return false;
  }

  // Ask for permission
  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      // Show a test notification to confirm it's working
      try {
        new Notification('¡Notificaciones activadas!', {
          body: 'Te avisaremos cuando tu pedido cambie de estado',
          icon: '/logo192.png'
        });
      } catch (e) {
        console.log('Could not show test notification:', e);
      }
    }
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Check if notifications are available and not blocked
export const getNotificationStatus = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'granted', 'denied', or 'default'
};

// Hook for CustomerOrdersPanel - detects changes when panel is open
export const useOrderNotifications = (mesaNumero, language = 'es') => {
  const previousOrdersRef = useRef({});
  const permissionGrantedRef = useRef(false);

  useEffect(() => {
    requestNotificationPermission().then(granted => {
      permissionGrantedRef.current = granted;
    });
  }, []);

  const checkAndNotify = useCallback((newOrders) => {
    if (!permissionGrantedRef.current || !newOrders) return;

    const messages = notificationMessages[language] || notificationMessages.es;

    newOrders.forEach(order => {
      const previousStatus = previousOrdersRef.current[order.id];
      const currentStatus = order.estado;

      if (previousStatus && previousStatus !== currentStatus) {
        const message = messages[currentStatus];
        if (message) {
          showNotification(message.title, message.body);
        }
      }

      previousOrdersRef.current[order.id] = currentStatus;
    });
  }, [language]);

  const initializeOrders = useCallback((orders) => {
    if (!orders) return;
    orders.forEach(order => {
      previousOrdersRef.current[order.id] = order.estado;
    });
  }, []);

  return { checkAndNotify, initializeOrders };
};

// Export the bell sound function for use in admin panels
export { playHotelBellSound };

export default useOrderNotifications;
